import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { getEnduserProjectGroupsCompletion } from "../state/enduserStatsSlice";

const EnduserFunnelChart = () => {
  const { t } = useTranslation();
  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);

  // Transform parcours data into chart data
  const chartData = useMemo(() => {
    if (!projectGroupsCompletion || projectGroupsCompletion.length === 0) return [];

    return projectGroupsCompletion.map((pg) => {
      const progress = Math.round(pg.progressPercentageOnLatestBuild ?? 0);

      return {
        id: pg.projectGroupId,
        name: pg.projectGroupName,
        shortName:
          pg.projectGroupName.length > 15
            ? pg.projectGroupName.substring(0, 15) + "..."
            : pg.projectGroupName,
        progress,
      };
    });
  }, [projectGroupsCompletion]);

  // Get bar color based on progress
  const getBarColor = (progress: number) => {
    if (progress === 100) return "#00c889"; // Green - completed
    if (progress > 0) return "#00c889"; // Green with opacity handled by the bar
    return "#e5e7eb"; // Gray - not started
  };

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  // Custom tooltip
  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: Array<{ payload: { name: string; progress: number } }>;
  }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-gray-900 text-sm">{data.name}</p>
        <p className="text-sm text-gray-600">
          {t("enduserStats.progress")}:{" "}
          <span className="font-semibold text-green-600">{data.progress}%</span>
        </p>
      </div>
    );
  };

  return (
    <div className="h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
        >
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickLine={false}
            axisLine={{ stroke: "#e5e7eb" }}
            tickFormatter={(value) => `${value}%`}
          />
          <YAxis
            type="category"
            dataKey="shortName"
            tick={{ fontSize: 11, fill: "#374151" }}
            tickLine={false}
            axisLine={false}
            width={100}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0, 200, 137, 0.1)" }} />
          <Bar
            dataKey="progress"
            radius={[0, 4, 4, 0]}
            maxBarSize={24}
            background={{ fill: "#f3f4f6" }}
          >
            {chartData.map((entry) => (
              <Cell key={entry.id} fill={getBarColor(entry.progress)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EnduserFunnelChart;
